import { CircleFadingArrowUp, Leaf, MoveUpRight } from 'lucide-react';
import { signIn } from 'next-auth/react';

import { Button } from '@/components/button';
import { Heading } from '@/components/heading';
import { Link } from '@/components/link';
import { Text } from '@/components/text';
import { MODE_AIR, MODE_WATER, UNIT_CO2, UNIT_CO2_KG, UNIT_CO2_TEU } from '@/lib/constants';
import { Co2Emission } from '@/lib/models/emissions/types';
import { transformCo2EmissionToRadialData } from '@/lib/transformers/emissions';
import { EmissionsModeBreakdown, EmissionStats, RadialChartData } from '@/types';
import { FuelBurnUnitType, SegmentModeType } from '@/types/api-types';
import { formatEmissionsString } from '@/utils/format/emissions';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { EmissionsRadialChart } from '../radial-chart';
import { EmissionsRangeBar } from '../range-bar';
import { transformModeBreakdownToRadialData } from './utils';

export const getAssumptionsMessage = (unit: FuelBurnUnitType) => {
  switch (unit) {
    case UNIT_CO2_KG:
      return 'Emissions are calculated based on 100kg of cargo assuming a vehicle load factor of 70%.';
    case UNIT_CO2_TEU:
      return 'Emissions are calculated based on 1 TEU assuming a vessel load factor of 70% for the entire journey.';
    default:
      return 'Emissions are calculated based on 100kg of cargo assuming a vehicle load factor of 90%.';
  }
};

export const getWtwLabel = (mode: SegmentModeType) => {
  switch (mode) {
    case MODE_AIR:
      return 'Well to Wake (WTW) Emissions';
    case MODE_WATER:
      return 'Well to Wake (WTW) Emissions';
    default:
      return 'Well to Wake (WTW) Emissions';
  }
};

export const calcPercentDifference = (value = 0, average = 0): number => {
  let diff = 0;
  if (value < average) {
    diff = ((average - value) / average) * 100;
  } else {
    diff = ((value - average) / average) * 100;
  }
  return diff < 1 ? Number(diff.toFixed(1)) : Math.round(diff);
};

export interface EmissionsDetailBreakdownProps {
  mode: SegmentModeType;
  value?: number;
  unit?: FuelBurnUnitType;
  comparison?: EmissionStats;
  modeBreakdown?: EmissionsModeBreakdown;
  wtwBreakdown?: Co2Emission;
  averageMsg?: string;
  percentage?: number;
  isLocked?: boolean;
  isInCard?: boolean;
}

const EmissionsDetailBreakdown = ({
  mode,
  value = 0,
  unit,
  comparison,
  modeBreakdown,
  wtwBreakdown,
  averageMsg = 'Average for routes like this',
  percentage = 0,
  isLocked = false,
  isInCard = false,
}: EmissionsDetailBreakdownProps) => {
  let chartData: RadialChartData[] | undefined;
  let chartTitle = 'Breakdown by mode';

  if (modeBreakdown) {
    chartData = transformModeBreakdownToRadialData(modeBreakdown);
  } else if (wtwBreakdown) {
    chartData = transformCo2EmissionToRadialData(wtwBreakdown);
    chartTitle = getWtwLabel(mode);
  }

  if (isInCard) {
    trackUser.event('Emissions: Popout open', { mode, isLocked });
  }

  const handleClickSignin = () => {
    signIn();

    trackUser.event('Go to: Sign-in', {
      location: 'Emissions Popout',
    });
  };

  const {
    base,
    header,
    headerIcon,
    content,
    definitionList,
    definitionTitle,
    definitionDescription,
    chartContainer,
    footer,
    footerText,
    footerLink,
    headerText,
    blurredStat,
  } = styles({ isInCard });

  return (
    <div className={base()}>
      <header className={header()}>
        <Leaf className={headerIcon()} />
        <Heading variant="h5" className={headerText()}>
          Emissions estimate
        </Heading>
      </header>
      <article className={content()}>
        {isLocked ? (
          <div className="-m-4 mb-0 rounded-b-md bg-states-warning-backgroundColor p-4">
            <div className="flex items-center gap-2">
              <CircleFadingArrowUp className="size-4" />
              <Heading variant="h5" className="mb-1 text-baseSm font-medium">
                Account required
              </Heading>
            </div>
            <Text variant="secondary" className="mb-3 text-sm leading-snug">
              Create a free account to see more emissions data including comparisons and breakdowns
            </Text>
            <Button size="small" iconRight={<MoveUpRight className="size-4" />} onClick={() => handleClickSignin()}>
              Sign up for an account
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-4">
              {value && (
                <dl className={definitionList({ isPrimary: true })}>
                  <dt className={definitionTitle()}>Total emissions</dt>
                  <dd className={definitionDescription()}>{formatEmissionsString(value, unit)}</dd>
                </dl>
              )}
              {comparison && (
                <dl className={definitionList()}>
                  <dt className={definitionTitle()}>{averageMsg}</dt>
                  <dd className={definitionDescription()}>
                    {!isLocked ? (
                      formatEmissionsString(comparison.average, unit)
                    ) : (
                      <span className={blurredStat()}>N/A {UNIT_CO2}</span>
                    )}
                  </dd>
                </dl>
              )}
              <EmissionsRangeBar percentage={[percentage]} />
            </div>
            {chartData && (
              <div className={chartContainer()}>
                <EmissionsRadialChart
                  data={chartData}
                  title={chartTitle}
                  unit={unit}
                  variant={!isInCard ? 'large' : 'default'}
                />
              </div>
            )}
          </>
        )}
      </article>
      <footer className={footer()}>
        {unit && (
          <Text variant="sm" className={footerText()}>
            {getAssumptionsMessage(unit)} Learn more about how we calculate these numbers.
          </Text>
        )}
        <Link className={footerLink()} href="/features/emissions/methodology">
          Our emissions methodology →
        </Link>
      </footer>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'text-left',
    content: 'block p-4',
    header: 'flex items-center gap-1.5 px-4 py-3',
    headerText: 'm-0 font-medium',
    headerIcon: 'size-4 fill-green-300',
    footer: 'p-4 pt-0',
    footerText: 'm-0 text-xs leading-snug opacity-60',
    footerLink: 'mt-2 inline-block text-xs leading-snug underline opacity-60 hover:opacity-100',
    chartContainer: '',
    definitionList: 'm-0 grid grid-cols-2 text-sm text-text-secondary last-of-type:mb-0',
    definitionTitle: 'm-0 flex min-w-44 items-center p-0',
    definitionDescription: 'justify-right m-0 flex items-center p-0 text-right',
    blurredStat: 'blur-[3px]',
  },
  variants: {
    isPrimary: {
      true: {
        definitionList: 'text-baseSm font-medium text-text-primary',
      },
    },
    isInCard: {
      true: {
        header: 'border-b border-grey-200',
        chartContainer: 'block border-t border-grey-200 pt-3',
      },
      false: {
        header: 'px-6 pb-0 pt-6',
        headerText: 'text-baseLg',
        headerIcon: 'size-5',
        content: 'grid grid-cols-2 gap-8 p-6',
        chartContainer: 'grid place-items-center',
        footer: 'px-6',
        footerText: 'text-sm',
      },
    },
  },
});

export { EmissionsDetailBreakdown };
